import { Component, ViewEncapsulation } from '@angular/core';

import { translateCognitoError } from '../../utils/auth-utils';
import { CommonModule } from '@angular/common';
import { Router, RouterLink } from '@angular/router';
import { signUp, confirmSignUp, resendSignUpCode } from 'aws-amplify/auth';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { EnvironmentConfigService } from '../../core/config/environment-config.service';
import { AuthenticationService } from '../../services/authentication/authentication.service';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'app-create-account',
  standalone: true,
  imports: [CommonModule, RouterLink],
  templateUrl: './create-account.component.html',
  styleUrl: './create-account.component.css',
  encapsulation: ViewEncapsulation.None,
})
export class CreateAccountComponent {
  isPending = false;
  error: string | null = null;
  success: string | null = null;

  // Confirmation step state
  showConfirm = false;
  emailForConfirm: string = '';
  isConfirming = false;
  isResending = false;
  confirmError: string | null = null;
  confirmSuccess: string | null = null;

  // For redirecting to checkout after confirmation
  private stripeMonthlyPriceId: string = '';
  private stripeAnnualPriceId: string = '';
  private apiBaseUrl: string = '';


  constructor(
    private router: Router,
    private httpClient: HttpClient,
    private environmentConfigService: EnvironmentConfigService,
    private authService: AuthenticationService
  ) {
    // Load environment values
    this.stripeMonthlyPriceId = this.environmentConfigService.StripeMonthlyPriceId;
    this.stripeAnnualPriceId = this.environmentConfigService.StripeAnnualPriceId;
    this.apiBaseUrl = this.environmentConfigService.apiUrl;
  }

  async onSubmit(event: Event) {
    event.preventDefault();
    this.error = null;
    this.success = null;
    const form = event.target as HTMLFormElement;
    const fd = new FormData(form);

    const name = (fd.get('name') as string)?.trim();
    const family_name = (fd.get('family_name') as string)?.trim();
    const email = (fd.get('email') as string)?.trim();
    const confirm_email = (fd.get('confirm_email') as string)?.trim();
    const phone_number = (fd.get('phone_number') as string)?.trim();
    const address = (fd.get('address') as string)?.trim();
    const birthdate = (fd.get('birthdate') as string)?.trim();
    const gender = (fd.get('gender') as string)?.trim();
    const postalcode = (fd.get('postalcode') as string)?.trim();
    const state = (fd.get('state') as string)?.trim();
    const city = (fd.get('city') as string)?.trim();

    const password = fd.get('password') as string;
    const confirm_password = fd.get('confirm_password') as string;

    if (!email || !password) {
      this.error = 'Informe e-mail e senha.';
      return;
    }
    if (email !== confirm_email) {
      this.error = 'Os e-mails não coincidem.';
      return;
    }
    if (password !== confirm_password) {
      this.error = 'As senhas não coincidem.';
      return;
    }

    this.isPending = true;
    try {

      await signUp({
        username: email,
        password,
        options: {
          userAttributes: {
            email,
            name,
            family_name,
            phone_number,
            address,
            birthdate,
            gender,
            // Send optional custom attributes if your user pool defines them
            ...(postalcode ? { 'custom:postalcode': postalcode } : {}),
            ...(state ? { 'custom:state': state } : {}),
            ...(city ? { 'custom:city': city } : {}),
          } as any,
          // autoSignIn is false by default; keep the standard confirm-by-email flow
        },
      });

      // Switch to the confirmation step instead of redirecting immediately
      this.emailForConfirm = email;
      this.showConfirm = true;
      this.success = null;
      this.confirmSuccess = 'Conta criada! Enviamos um código para ' + email + '. Insira-o abaixo para confirmar seu cadastro.';
      // Optionally clear the form fields
      form.reset();
    } catch (e: any) {
      this.error = translateCognitoError(e, e?.message || 'Falha ao criar conta. Tente novamente.');
    } finally {
      this.isPending = false;
    }
  }

  async onConfirmSubmit(event: Event) {
    event.preventDefault();
    this.confirmError = null;
    this.confirmSuccess = null;
    const form = event.target as HTMLFormElement;
    const fd = new FormData(form);
    const code = (fd.get('confirmation_code') as string)?.trim();
    if (!code) {
      this.confirmError = 'Informe o código de confirmação.';
      return;
    }
    this.isConfirming = true;
    try {
      await confirmSignUp({ username: this.emailForConfirm, confirmationCode: code });

      // Check if a plan was selected before account creation
      let pendingPlan: { planType?: 'monthly' | 'annual'; ts?: number } | null = null;
      try {
        const raw = localStorage.getItem('pendingSubscription');
        pendingPlan = raw ? JSON.parse(raw) : null;
      } catch (_) { pendingPlan = null; }

      if (pendingPlan && pendingPlan.planType) {
        // Do not auto sign-in or store password. Ask user to login to continue checkout.
        this.confirmSuccess = 'Conta confirmada! Entre para finalizar o pagamento.';
        // Keep pendingSubscription so we can resume after login
        setTimeout(() => this.router.navigate(['/login']), 1200);
        return;
      }

      // No pending plan; go to login as before
      this.confirmSuccess = 'Conta confirmada com sucesso! Redirecionando para o login...';
      setTimeout(() => this.router.navigate(['/login']), 1200);
    } catch (e: any) {
      this.confirmError = translateCognitoError(e, e?.message || 'Falha ao confirmar. Verifique o código e tente novamente.');
    } finally {
      this.isConfirming = false;
    }
  }

  async resendCode() {
    this.confirmError = null;
    this.confirmSuccess = null;
    this.isResending = true;
    try {
      await resendSignUpCode({ username: this.emailForConfirm });
      this.confirmSuccess = 'Novo código enviado para ' + this.emailForConfirm + '.';
    } catch (e: any) {
      this.confirmError = translateCognitoError(e, e?.message || 'Falha ao reenviar código. Tente novamente.');
    } finally {
      this.isResending = false;
    }
  }

  backToEdit() {
    // Allow user to go back to edit their email/details
    this.showConfirm = false;
    this.confirmError = null;
    this.confirmSuccess = null;
  }

  private clearPendingSubscription() {
    try { localStorage.removeItem('pendingSubscription'); } catch (_) {}
  }

  private async redirectToCheckout(planType: 'monthly' | 'annual'): Promise<void> {
    // Determine which price ID to use based on the selected plan
    const priceId = planType === 'monthly' ? this.stripeMonthlyPriceId : this.stripeAnnualPriceId;

    const checkoutData = {
      priceId: priceId,
      successUrl: `${window.location.origin}/checkout`,
      cancelUrl: `${window.location.origin}/planos`,
    };

    let headers = new HttpHeaders()
      .set('content-type', 'application/json')
      .set('Access-Control-Allow-Origin', '*');

    try {
      const idToken = await this.authService.getIdToken();
      if (idToken) {
        headers = headers.set('Authorization', `Bearer ${idToken}`);
      }
    } catch (error) {
      console.error('Error getting authentication token:', error);
    }

    try {
      const response = await firstValueFrom(
        this.httpClient.post<{ url: string }>(
          `${this.apiBaseUrl}/create-checkout-session`,
          checkoutData,
          { headers }
        )
      );
      if (response && response.url) {
        this.clearPendingSubscription();
        window.location.href = response.url;
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (error) {
      console.error('Error creating checkout session:', error);
      // As a fallback, go to plans page
      this.clearPendingSubscription();
      await this.router.navigate(['/planos']);
    }
  }
}
